import {
  type JSX,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ArrowsPointingOutIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { useTheme } from '~/hooks/useTheme';

interface MermaidDiagramProps {
  chart: string;
}

const MIN_SCALE = 0.25;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.15;
const INITIAL_MODAL_SCALE = 2;

interface DiagramModalProps {
  containerRef: RefObject<HTMLDivElement | null>;
  dragRef: MutableRefObject<DragState | null>;
  onClose: () => void;
  onPointerDown: (e: ReactPointerEvent) => void;
  onPointerMove: (e: ReactPointerEvent) => void;
  onPointerUp: () => void;
  onReset: () => void;
  onWheel: (e: ReactWheelEvent) => void;
  scale: number;
  translate: { x: number; y: number };
}

interface DragState {
  origX: number;
  origY: number;
  startX: number;
  startY: number;
}

function DiagramModal({
  containerRef,
  dragRef,
  onClose,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onReset,
  onWheel,
  scale,
  translate,
}: DiagramModalProps): JSX.Element {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop dismiss; Escape handled separately
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Toolbar */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- prevents backdrop close */}
      <div
        className="flex items-center justify-between px-4 py-2 bg-surface/90 border-b border-default"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 text-sm text-muted">
          <span>Scroll to zoom</span>
          <span className="text-default">|</span>
          <span>Drag to pan</span>
          <span className="text-default">|</span>
          <span>{Math.round(scale * 100)}%</span>
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-primary hover:underline cursor-pointer"
          >
            Reset
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-md text-muted hover:text-foreground hover:bg-default/40 transition-colors cursor-pointer"
          aria-label="Close diagram viewer"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Pannable / zoomable area */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- pan/zoom via pointer events */}
      <div
        className="flex-1 overflow-hidden cursor-grab active:cursor-grabbing"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onClick={e => e.stopPropagation()}
      >
        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transformOrigin: 'center center',
            transition: dragRef.current ? 'none' : 'transform 0.1s ease-out',
          }}
        >
          <div ref={containerRef} className="mermaid-container" />
        </div>
      </div>
    </div>
  );
}

/** Parse an SVG string into a DOM element safely via DOMParser. */
function parseSvg(svg: string): Element | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svg, 'image/svg+xml');
  const errorNode = doc.querySelector('parsererror');
  if (errorNode) return null;
  return doc.documentElement;
}

/**
 * Renders a Mermaid diagram from a definition string.
 *
 * Lazy-loads the mermaid library on first use to avoid bloating the initial
 * bundle (~2 MB). Re-renders when the theme or chart definition changes.
 * Click to open a full-screen modal with pan & zoom.
 */
export function MermaidDiagram({ chart }: MermaidDiagramProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const { isDarkMode } = useTheme();
  const [error, setError] = useState<string | null>(null);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Pan & zoom state for the modal
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const dragRef = useRef<DragState | null>(null);

  // Unique ID for mermaid's render target (must be a valid DOM id)
  const reactId = useId();
  const diagramId = `mermaid-${reactId.replace(/:/g, '')}`;

  useEffect(() => {
    let cancelled = false;

    async function renderDiagram(): Promise<void> {
      try {
        const mermaid = (await import('mermaid')).default;

        mermaid.initialize({
          startOnLoad: false,
          theme: isDarkMode ? 'dark' : 'default',
          fontFamily: 'inherit',
          securityLevel: 'strict',
        });

        const { svg } = await mermaid.render(diagramId, chart.trim());

        if (!cancelled) {
          setSvgContent(svg);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to render diagram');
        }
      }
    }

    void renderDiagram();

    return (): void => {
      cancelled = true;
    };
  }, [chart, isDarkMode, diagramId]);

  // Insert SVG into inline container via DOM API, scaled to fill width
  useEffect(() => {
    if (!svgContent || !containerRef.current) return;
    const svgEl = parseSvg(svgContent);
    if (svgEl) {
      const svg = svgEl as SVGSVGElement;
      svg.style.width = '100%';
      svg.style.height = 'auto';
      svg.style.maxWidth = '100%';
      containerRef.current.replaceChildren(svg);
    }
  }, [svgContent]);

  // Insert SVG into modal container via DOM API
  useEffect(() => {
    if (!svgContent || !modalContainerRef.current || !isOpen) return;
    const svgEl = parseSvg(svgContent);
    if (svgEl) {
      const svg = svgEl as SVGSVGElement;
      svg.style.width = '100%';
      svg.style.height = '100%';
      modalContainerRef.current.replaceChildren(svg);
    }
  }, [svgContent, isOpen]);

  const openModal = useCallback(() => {
    setScale(INITIAL_MODAL_SCALE);
    setTranslate({ x: 0, y: 0 });
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKey);
    return (): void => {
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, closeModal]);

  const handleWheel = useCallback((e: ReactWheelEvent) => {
    e.preventDefault();
    setScale(prev => {
      const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      return Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev + delta));
    });
  }, []);

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (e.button !== 0) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        origX: translate.x,
        origY: translate.y,
        startX: e.clientX,
        startY: e.clientY,
      };
    },
    [translate],
  );

  const handlePointerMove = useCallback((e: ReactPointerEvent) => {
    if (!dragRef.current) return;
    setTranslate({
      x: dragRef.current.origX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.origY + (e.clientY - dragRef.current.startY),
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const resetView = useCallback(() => {
    setScale(INITIAL_MODAL_SCALE);
    setTranslate({ x: 0, y: 0 });
  }, []);

  if (error) {
    return (
      <div className="my-4 p-4 rounded-lg border border-warning/30 bg-warning/5 text-sm text-muted">
        <p className="font-medium text-warning mb-1">Diagram render error</p>
        <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{error}</pre>
      </div>
    );
  }

  return (
    <>
      {/* Inline preview — click to expand */}
      <div
        className="my-4 w-full overflow-x-auto group relative cursor-pointer rounded-lg border border-transparent hover:border-primary/30 transition-colors"
        onClick={openModal}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') openModal();
        }}
      >
        <div ref={containerRef} className="mermaid-container w-full" />
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-surface/80 backdrop-blur-sm rounded-md px-2 py-1 flex items-center gap-1 text-xs text-muted pointer-events-none">
          <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
          Click to zoom
        </div>
      </div>

      {/* Full-screen zoom modal */}
      {isOpen &&
        createPortal(
          <DiagramModal
            containerRef={modalContainerRef}
            scale={scale}
            translate={translate}
            dragRef={dragRef}
            onClose={closeModal}
            onWheel={handleWheel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onReset={resetView}
          />,
          document.body,
        )}
    </>
  );
}
